import { getBackendUrl as getMetaStageBackendUrl } from "../../config";
import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Paper,
  Typography,
  Divider,
  Chip,
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from "@material-ui/core";

import axios from "axios";

const riderApi = axios.create({
  baseURL: getMetaStageBackendUrl(),
  timeout: 10000
});

const RiderTickets = () => {
  const [tickets, setTickets] = useState([]);
  const [availableTickets, setAvailableTickets] = useState([]);
  const [availableError, setAvailableError] = useState("");
  const [claimingId, setClaimingId] = useState(null);
  const [finishingId, setFinishingId] = useState(null);
  const [finishError, setFinishError] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState("");
  const [messages, setMessages] = useState([]);
  const [chatLoading, setChatLoading] = useState(false);
  const [chatError, setChatError] = useState("");

  const loadTickets = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const storedToken = localStorage.getItem("token");

      if (!storedToken) {
        throw new Error("No existe una sesion activa.");
      }

      const token = JSON.parse(storedToken);

      const { data } = await riderApi.get("/tickets/mine", {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (!Array.isArray(data.tickets)) {
        throw new Error("Respuesta de pedidos no valida.");
      }

      setTickets(data.tickets);
    } catch (err) {
      setTickets([]);
      setError("No fue posible cargar tus pedidos. Intenta nuevamente.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTickets();
  }, [loadTickets]);

  const loadAvailable = useCallback(async () => {
    setAvailableError("");

    try {
      const token = JSON.parse(localStorage.getItem("token"));

      const { data } = await riderApi.get("/tickets/available", {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!Array.isArray(data.tickets)) {
        throw new Error("Respuesta no valida");
      }

      setAvailableTickets(data.tickets);
    } catch (err) {
      setAvailableTickets([]);
      setAvailableError("No fue posible consultar los pedidos disponibles.");
    }
  }, []);

  useEffect(() => {
    loadAvailable();
  }, [loadAvailable]);

  const claimTicket = async ticketId => {
    setClaimingId(ticketId);
    setAvailableError("");

    try {
      const token = JSON.parse(localStorage.getItem("token"));

      await riderApi.post(
        `/tickets/${ticketId}/claim`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      await Promise.all([loadTickets(), loadAvailable()]);
    } catch (err) {
      const status = err.response?.status;

      setAvailableError(
        status === 409
          ? "El pedido ya fue tomado o tienes otro servicio activo."
          : status === 403
          ? "No tienes permiso para tomar este pedido."
          : "No fue posible tomar el pedido."
      );

      await loadAvailable();
    } finally {
      setClaimingId(null);
    }
  };

  const loadMessages = async ticket => {
    setChatLoading(true);
    setChatError("");

    try {
      const token = JSON.parse(localStorage.getItem("token"));

      const { data } = await riderApi.get(
        `/messages/${ticket.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (!Array.isArray(data.messages)) {
        throw new Error("Respuesta no valida");
      }

      setMessages(data.messages);
    } catch (err) {
      setMessages([]);

      if ([401, 403, 404].includes(err.response?.status)) {
        setSelectedTicket(null);
        setChatError("");
        setDetailError("Ya no tienes acceso a este pedido.");
      } else {
        setChatError("No fue posible cargar la conversación.");
      }
    } finally {
      setChatLoading(false);
    }
  };

  useEffect(() => {
    if (!selectedTicket) return;

    let active = true;
    let checking = false;

    const verifyAccess = async () => {
      if (checking || !active) return;
      checking = true;

      try {
        const token = JSON.parse(localStorage.getItem("token"));
        if (!token) throw new Error("Sin sesion");

        await riderApi.get(
          `/tickets/${selectedTicket.id}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } catch (err) {
        if (active) {
          setMessages([]);
          setSelectedTicket(null);
          setDetailError(
            [401, 403, 404].includes(err.response?.status)
              ? "Ya no tienes acceso a este pedido."
              : "No se pudo verificar el acceso. Conversacion cerrada por seguridad."
          );
        }
      } finally {
        checking = false;
      }
    };

    const interval = setInterval(verifyAccess, 5000);

    return () => {
      active = false;
      clearInterval(interval);
    };
  }, [selectedTicket]);

  const finishTicket = async ticket => {
    if (!ticket || finishingId !== null) return;

    const confirmed = window.confirm(
      `¿Finalizar la conversación #${ticket.id}?\n\n` +
      "Después de finalizarla, ya no podrás consultar sus mensajes."
    );

    if (!confirmed) return;

    setFinishingId(ticket.id);
    setFinishError("");

    try {
      const storedToken = localStorage.getItem("token");

      if (!storedToken) {
        throw new Error("No se encontró una sesión válida.");
      }

      const token = JSON.parse(storedToken);

      await riderApi.post(
        `/tickets/${ticket.id}/finish`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      setSelectedTicket(null);
      setMessages([]);
      setChatError("");

      await Promise.all([
        loadTickets(),
        loadAvailable()
      ]);
    } catch (err) {
      setFinishError(
        err.response?.data?.error ||
        err.message ||
        "No se pudo finalizar la conversación."
      );
    } finally {
      setFinishingId(null);
    }
  };

  const openTicket = async ticket => {
    setSelectedTicket(null);
    setMessages([]);
    setChatError("");
    setDetailError("");
    setDetailLoading(true);

    try {
      const storedToken = localStorage.getItem("token");
      if (!storedToken) throw new Error("Sin sesión");

      const token = JSON.parse(storedToken);

      await riderApi.get(
        `/tickets/${ticket.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const { data } = await riderApi.get(
        `/messages/${ticket.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (!Array.isArray(data.messages)) {
        throw new Error("Respuesta no valida");
      }

      setMessages(data.messages);
      setSelectedTicket(ticket);
    } catch (err) {
      setDetailError(
        err.response?.status === 403
          ? "Acceso denegado: este pedido no está asignado a tu cuenta."
          : "No fue posible abrir este pedido."
      );
    } finally {
      setDetailLoading(false);
    }
  };

  return (
    <Box p={2}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <Typography variant="h5">
          Mis pedidos
        </Typography>

        <Button
          variant="outlined"
          color="primary"
          onClick={loadTickets}
          disabled={loading}
        >
          Actualizar
        </Button>
      </Box>

      <Paper variant="outlined">
        <Box p={2}>
          <Typography variant="subtitle1">
            Pedidos asignados
          </Typography>

          <Divider style={{ margin: "12px 0" }} />

          {loading && (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress size={28} />
            </Box>
          )}

          {!loading && error && (
            <Typography color="error">
              {error}
            </Typography>
          )}

          {!loading && !error && tickets.length === 0 && (
            <Typography color="textSecondary">
              No tienes pedidos asignados actualmente.
            </Typography>
          )}

          {!loading && !error && tickets.map(ticket => (
            <Box key={ticket.id} mb={2}>
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
              >
                <Typography>
                  Pedido #{ticket.id}
                </Typography>

                <Chip
                  label={ticket.status === "open"
                    ? "En servicio"
                    : ticket.status}
                  size="small"
                  color="primary"
                  variant="outlined"
                />
              </Box>

              {ticket.createdAt && (
                <Typography variant="body2" color="textSecondary">
                  Fecha: {new Date(ticket.createdAt).toLocaleString("es-MX")}
                </Typography>
              )}

              <Box mt={1}>
                <Button
                  variant="outlined"
                  color="primary"
                  disabled={detailLoading}
                  onClick={() => openTicket(ticket)}
                >
                  Abrir pedido
                </Button>
              </Box>

              <Divider style={{ marginTop: 12 }} />
            </Box>
          ))}
        </Box>
      </Paper>
      {availableTickets.length > 0 && (
        <Paper variant="outlined" style={{ marginTop: 16 }}>
          <Box p={2}>
            <Typography variant="subtitle1">
              Pedidos disponibles
            </Typography>

            <Typography variant="body2" color="textSecondary">
              Puedes tomar un pedido nuevo que nadie haya asignado.
            </Typography>

            <Divider style={{ margin: "12px 0" }} />

            {availableTickets.map(ticket => (
              <Box key={ticket.id} mb={2}>
                <Typography>
                  Pedido #{ticket.id}
                </Typography>

                <Typography variant="body2" color="textSecondary">
                  Estado: pendiente
                </Typography>

                <Box mt={1}>
                  <Button
                    variant="contained"
                    color="primary"
                    disabled={claimingId !== null || tickets.length > 0}
                    onClick={() => claimTicket(ticket.id)}
                  >
                    {claimingId === ticket.id
                      ? "Tomando pedido..."
                      : "Tomar pedido"}
                  </Button>
                </Box>

                <Divider style={{ marginTop: 12 }} />
              </Box>
            ))}
          </Box>
        </Paper>
      )}

      {availableError && (
        <Box mt={2}>
          <Typography color="error">
            {availableError}
          </Typography>
        </Box>
      )}

      <Dialog
        open={Boolean(selectedTicket)}
        onClose={() => setSelectedTicket(null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          Pedido #{selectedTicket?.id}
        </DialogTitle>

        <DialogContent dividers>
          <Typography>
            Estado: {selectedTicket?.status === "open"
              ? "En servicio"
              : selectedTicket?.status}
          </Typography>

          <Box mt={2}>
            <Typography color="textSecondary">
              Conversación de pruebas · Solo lectura
            </Typography>

            <Box mt={2} mb={2}>
              <Button
                size="small"
                variant="outlined"
                disabled={chatLoading}
                onClick={() => loadMessages(selectedTicket)}
              >
                Actualizar conversación
              </Button>
            </Box>

            {chatLoading && <CircularProgress size={22} />}

            {chatError && (
              <Typography color="error">{chatError}</Typography>
            )}

            {!chatLoading && !chatError && messages.length === 0 && (
              <Typography color="textSecondary">
                No hay mensajes en este pedido.
              </Typography>
            )}

            {!chatLoading && messages.map(message => (
              <Paper
                key={message.id}
                variant="outlined"
                style={{
                  padding: 12,
                  marginBottom: 10,
                  backgroundColor: message.fromMe ? "#e8f1ff" : "#f5f5f5"
                }}
              >
                <Typography variant="caption" color="textSecondary">
                  {message.fromMe ? "Enviado" : "Recibido"}
                </Typography>

                <Typography style={{ whiteSpace: "pre-wrap" }}>
                  {message.body}
                </Typography>

                <Typography variant="caption" color="textSecondary">
                  {new Date(message.createdAt).toLocaleString("es-MX")}
                </Typography>
              </Paper>
            ))}

            <Typography variant="caption" color="textSecondary">
              Sin conexión a WhatsApp. No es posible enviar mensajes.
            </Typography>
          </Box>
        </DialogContent>

        {finishError && (
          <Box px={3} pb={1}>
            <Typography color="error">
              {finishError}
            </Typography>
          </Box>
        )}

        <DialogActions>
          {selectedTicket?.status === "open" && (
            <Button
              variant="contained"
              color="secondary"
              disabled={finishingId !== null}
              onClick={() => finishTicket(selectedTicket)}
            >
              {finishingId === selectedTicket?.id
                ? "Finalizando..."
                : "Finalizar conversación"}
            </Button>
          )}

          <Button
            disabled={finishingId !== null}
            onClick={() => setSelectedTicket(null)}
          >
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>

      {detailLoading && (
        <Typography color="textSecondary">
          Verificando acceso al pedido...
        </Typography>
      )}

      {detailError && (
        <Typography color="error">
          {detailError}
        </Typography>
      )}
    </Box>
  );
};

export default RiderTickets;
